import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api, { formatBRL, formatKM } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Send, Loader2, TrendingDown, ArrowRight, GitCompareArrows, SlidersHorizontal, Check } from "lucide-react";

const SUGGESTIONS = [
  "Carro pra família até R$ 120 mil",
  "Econômico e automático pra cidade",
  "Melhor oferta abaixo da FIPE",
  "Primeiro carro, baixa quilometragem",
];

function belowPct(car) {
  return car.fipe_price && car.preco < car.fipe_price
    ? Math.round((car.fipe_price - car.preco) / car.fipe_price * 100) : 0;
}

function RecCard({ car, onOpen, selected, onToggle }) {
  const below = belowPct(car);
  return (
    <div className={`w-full bg-white border rounded-sm p-3 flex items-center gap-3 transition-colors ${selected ? "border-blaze" : "border-border"}`}>
      <button type="button" onClick={onToggle} data-testid={`assistant-compare-toggle-${car.id}`}
        className={`shrink-0 w-5 h-5 rounded-sm border flex items-center justify-center transition-colors ${selected ? "bg-blaze border-blaze" : "border-border hover:border-blaze"}`}
        aria-label="Selecionar para comparar">
        {selected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
      </button>
      <button onClick={onOpen} data-testid={`assistant-rec-${car.id}`}
        className="flex-1 min-w-0 text-left flex items-center justify-between gap-3 hover:text-blaze transition-colors">
        <div className="min-w-0">
          <p className="font-700 text-sm text-obsidian truncate">{car.marca} {car.modelo}</p>
          <p className="text-xs text-muted-foreground truncate">{car.ano} · {formatKM(car.km)}{car.cidade ? ` · ${car.cidade}` : ""}</p>
          <div className="mt-1 flex items-center gap-2">
            <span className="font-800 text-sm text-obsidian">{formatBRL(car.preco)}</span>
            {below > 0 && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-700 bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-sm">
                <TrendingDown className="w-3 h-3" /> {below}% FIPE
              </span>
            )}
          </div>
        </div>
        <ArrowRight className="w-4 h-4 text-blaze shrink-0" />
      </button>
    </div>
  );
}

function filtersToQuery(f) {
  const p = new URLSearchParams();
  if (f.marca) p.set("marca", f.marca);
  if (f.preco_max) p.set("preco_max", String(f.preco_max));
  if (f.cambio) p.set("cambio", f.cambio);
  if (f.ano_min) p.set("ano_min", String(f.ano_min));
  return p.toString();
}

function filtersLabel(f) {
  const parts = [];
  if (f.marca) parts.push(f.marca);
  if (f.preco_max) parts.push(`até ${formatBRL(f.preco_max)}`);
  if (f.cambio) parts.push(f.cambio);
  if (f.ano_min) parts.push(`${f.ano_min}+`);
  return parts.join(" · ");
}

function CompareRow({ label, values, highlightBest, better }) {
  let bestIdx = -1;
  if (highlightBest) {
    let best = null;
    values.forEach((v, i) => {
      if (v == null || isNaN(v)) return;
      if (best == null || (better === "low" ? v < best : v > best)) { best = v; bestIdx = i; }
    });
  }
  return (
    <tr className="border-t border-border">
      <td className="py-2.5 pr-3 text-xs font-700 uppercase tracking-wide text-muted-foreground whitespace-nowrap align-top">{label}</td>
      {values.map((v, i) => (
        <td key={i} className={`py-2.5 px-3 text-sm align-top ${i === bestIdx ? "font-800 text-emerald-700" : "font-600 text-obsidian"}`}>
          {v.display}
        </td>
      ))}
    </tr>
  );
}

function CompareTable({ cars }) {
  const rows = [
    { label: "Preço", get: (c) => ({ raw: c.preco, display: formatBRL(c.preco) }), best: true, better: "low" },
    { label: "Ano", get: (c) => ({ raw: c.ano, display: c.ano }), best: true, better: "high" },
    { label: "KM", get: (c) => ({ raw: c.km, display: formatKM(c.km) }), best: true, better: "low" },
    { label: "Câmbio", get: (c) => ({ display: c.cambio || "—" }) },
    { label: "Combustível", get: (c) => ({ display: c.combustivel || "—" }) },
    { label: "FIPE", get: (c) => ({ display: c.fipe_price ? formatBRL(c.fipe_price) : "—" }) },
    { label: "Abaixo da FIPE", get: (c) => { const b = belowPct(c); return { raw: b, display: b > 0 ? `${b}%` : "—" }; }, best: true, better: "high" },
    { label: "Local", get: (c) => ({ display: [c.cidade, c.estado].filter(Boolean).join(" - ") || "—" }) },
  ];
  return (
    <div className="overflow-x-auto -mx-1">
      <table className="w-full border-collapse min-w-[420px]">
        <thead>
          <tr>
            <th className="text-left pb-2 pr-3"></th>
            {cars.map((c) => (
              <th key={c.id} className="text-left pb-2 px-3 min-w-[130px]">
                <p className="font-800 text-sm text-obsidian leading-tight">{c.marca} {c.modelo}</p>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <CompareRow key={r.label} label={r.label} highlightBest={r.best} better={r.better}
              values={cars.map(r.get)} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AssistantChat() {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]); // array de carros selecionados p/ comparar
  const [compareOpen, setCompareOpen] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const selectedIds = useMemo(() => new Set(selected.map((c) => c.id)), [selected]);

  const openCar = (id) => {
    api.post("/assistant/click", { listing_id: id }).catch(() => {});
    navigate(`/anuncio/${id}`);
  };
  const toggleSelect = (car) => {
    setSelected((prev) => {
      if (prev.some((c) => c.id === car.id)) return prev.filter((c) => c.id !== car.id);
      if (prev.length >= 4) return prev; // limite de 4 na comparação
      return [...prev, car];
    });
  };

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/chat", { message: msg, history });
      setMessages((prev) => [...prev, { role: "assistant", content: data.reply, recommendations: data.recommendations || [], filters: data.filters || {} }]);
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", content: "Ops, não consegui responder agora. Tente de novo em instantes.", recommendations: [], filters: {} }]);
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-white border border-border rounded-sm overflow-hidden shadow-sm" data-testid="assistant-chat">
      <div className="bg-obsidian text-white px-5 py-4 flex items-center gap-3">
        <span className="w-9 h-9 rounded-sm bg-blaze/20 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-blaze" />
        </span>
        <div>
          <p className="font-heading font-800 text-lg leading-none">Assistente PicCar</p>
          <p className="text-xs text-white/50 mt-1">Diga o que procura e eu acho o carro ideal entre os anúncios</p>
        </div>
      </div>

      <div ref={scrollRef} className="max-h-[360px] overflow-y-auto px-4 py-4 space-y-4 bg-secondary/30" data-testid="assistant-messages">
        {messages.length === 0 && (
          <div className="text-center py-6">
            <p className="text-sm text-muted-foreground mb-4">Experimente uma dessas ou escreva do seu jeito:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} data-testid="assistant-suggestion"
                  className="text-xs font-600 bg-white border border-border rounded-full px-3 py-1.5 hover:border-blaze hover:text-blaze transition-colors">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        <AnimatePresence initial={false}>
          {messages.map((m, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className={m.role === "user" ? "flex justify-end" : "flex justify-start"}>
              <div className={`max-w-[85%] ${m.role === "user" ? "" : "w-full"}`}>
                <div className={`px-4 py-2.5 rounded-sm text-sm leading-relaxed ${m.role === "user" ? "bg-blaze text-white" : "bg-white border border-border text-foreground"}`}>
                  {m.content}
                </div>
                {m.recommendations?.length > 0 && (
                  <div className="mt-2 space-y-2">
                    {m.recommendations.map((car) => (
                      <RecCard key={car.id} car={car} selected={selectedIds.has(car.id)}
                        onToggle={() => toggleSelect(car)} onOpen={() => openCar(car.id)} />
                    ))}
                    {m.filters && Object.keys(m.filters).length > 0 && (
                      <button onClick={() => navigate(`/buscar?${filtersToQuery(m.filters)}`)} data-testid="assistant-open-filters"
                        className="w-full flex items-center justify-between gap-2 text-left bg-obsidian text-white rounded-sm px-3 py-2.5 hover:bg-blaze transition-colors">
                        <span className="flex items-center gap-2 min-w-0">
                          <SlidersHorizontal className="w-4 h-4 shrink-0 text-blaze" />
                          <span className="text-xs font-700 truncate">Ver todos na Busca · {filtersLabel(m.filters)}</span>
                        </span>
                        <ArrowRight className="w-4 h-4 shrink-0" />
                      </button>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
        {loading && (
          <div className="flex justify-start">
            <div className="px-4 py-2.5 rounded-sm bg-white border border-border flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin text-blaze" /> Pensando...
            </div>
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="flex items-center justify-between gap-3 px-3 py-2.5 border-t border-border bg-blaze/5" data-testid="assistant-compare-bar">
          <p className="text-xs font-600 text-muted-foreground">
            {selected.length} selecionado(s){selected.length >= 4 ? " (máx.)" : ""}
          </p>
          <div className="flex items-center gap-2">
            <button onClick={() => setSelected([])} className="text-xs font-600 text-muted-foreground hover:text-obsidian" data-testid="assistant-compare-clear">Limpar</button>
            <Button size="sm" disabled={selected.length < 2} onClick={() => setCompareOpen(true)}
              className="bg-obsidian hover:bg-blaze text-white rounded-sm h-8 font-700" data-testid="assistant-compare-open">
              <GitCompareArrows className="w-4 h-4 mr-1.5" /> Comparar
            </Button>
          </div>
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 p-3 border-t border-border">
        <Input value={input} onChange={(e) => setInput(e.target.value)} disabled={loading}
          placeholder="Ex.: SUV automático até R$ 90 mil em SP" className="rounded-sm" data-testid="assistant-input" />
        <Button type="submit" disabled={loading || !input.trim()} className="bg-blaze hover:bg-obsidian text-white rounded-sm shrink-0" data-testid="assistant-send">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>

      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-w-2xl" data-testid="assistant-compare-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading font-800">
              <GitCompareArrows className="w-5 h-5 text-blaze" /> Comparar carros
            </DialogTitle>
          </DialogHeader>
          <CompareTable cars={selected} />
          <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {selected.map((c) => (
              <Button key={c.id} variant="outline" size="sm" onClick={() => { setCompareOpen(false); openCar(c.id); }}
                className="rounded-sm text-xs font-700 border-obsidian/15" data-testid={`assistant-compare-open-${c.id}`}>
                Ver {c.marca}
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
